import React from 'react';
import { Outlet, Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  CssBaseline,
  AppBar,
  Toolbar,
  Drawer,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Button,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import AssignmentIcon from '@mui/icons-material/Assignment';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { useAuth } from '../hooks/UserContext';
import { UserRole } from '../types/user';

const drawerWidth = 240;

const allNavItems = [
  // Managerial/Admin View
  { text: 'Dashboard', path: '/dashboard', icon: <DashboardIcon />, roles: ['OWNER', 'MANAGER'] },
  { text: 'Clients', path: '/clients', icon: <PeopleIcon />, roles: ['OWNER', 'MANAGER', 'OFFICE_ADMIN'] },
  { text: 'Projects', path: '/projects', icon: <BusinessCenterIcon />, roles: ['OWNER', 'MANAGER', 'OFFICE_ADMIN'] },
  { text: 'Tasks', path: '/tasks', icon: <AssignmentIcon />, roles: ['OWNER', 'MANAGER', 'OFFICE_ADMIN'] },
  { text: 'Employees', path: '/employees', icon: <GroupWorkIcon />, roles: ['OWNER', 'MANAGER'] },
  { text: 'Petty Cash', path: '/petty-cash', icon: <AttachMoneyIcon />, roles: ['OWNER', 'MANAGER'] },
  { text: 'Expenses', path: '/expenses', icon: <MonetizationOnIcon />, roles: ['OWNER', 'MANAGER', 'OFFICE_ADMIN'] },
  
  { text: 'My Tasks', path: '/my-tasks', icon: <AssignmentIcon />, roles: ['ENGINEER', 'SUPERVISOR', 'TECHNICIAN'] },
  { text: 'My Expenses', path: '/my-expenses', icon: <MonetizationOnIcon />, roles: ['OWNER', 'MANAGER', 'OFFICE_ADMIN', 'ENGINEER', 'SUPERVISOR', 'TECHNICIAN'] },
  { text: 'Payments', path: '/payments', icon: <AttachMoneyIcon />, roles: ['OWNER', 'MANAGER', 'OFFICE_ADMIN'] },
  
];

const getVisibleNavItems = (userRole: UserRole | undefined) => {
  if (!userRole) return [];
  return allNavItems.filter(item => item.roles.includes(userRole));
};


const MainLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isLoading, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const visibleNavItems = getVisibleNavItems(user?.role);

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar sx={{ position: 'relative' }}>
            <Typography variant="h6" noWrap component="div">
                MBMS
            </Typography>
            <Box sx={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
            }}>
                <Typography variant="h6" noWrap>
                    {user && `Hello, ${user.first_name} ${user.last_name}`}
                </Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <Button color="inherit" onClick={handleLogout}>
                Logout
            </Button>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box' },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto' }}>
          <List>
            {visibleNavItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to={item.path}
                  selected={location.pathname.startsWith(item.path)}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Toolbar />
        <Outlet /> {/* Child routes will render here */}
      </Box>
    </Box>
  );
};

export default MainLayout;